import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SurveyDetails from 'components/administration/components/blocks/components/block-type/components/Survey/components/survey-details/SurveyDetails'
import { SurveyEditorShell } from 'components/administration/components/blocks/components/block-type/components/Survey/components/editor-shell'
import { SurveyQuestionModal } from 'components/administration/components/blocks/components/block-type/components/Survey/components/question-modal'
import { SurveyQuestionRows } from 'components/administration/components/blocks/components/block-type/components/Survey/components/question-rows'
import type { IBlockInstanceModel } from 'models/block.model'
import { surveyTemplatesService } from 'api/administration/surveyTemplates'
import { useNotification } from 'hooks/notifications/useNotification'
import {
  fromEditorSave,
  toEditorQuestion,
  toSurveyTemplatePayload,
} from '../../common/converters'
import type { DraftQuestion } from '../../common/converters'
import { readValidationErrors } from '../../common/errors'
import type {
  SurveyTemplateDraft,
  SurveyTemplateErrors,
} from '../../common/types'
import { TRANSLATION_PREFIX } from '../../constants'
import { FieldError } from './styled'

interface IProps {
  initialDraft: SurveyTemplateDraft
  initialQuestions: DraftQuestion[]
  onDone: () => void
  onCancel: () => void
}

/**
 * Bank editor with the same two steps as the course survey block. Questions
 * are kept in a local draft and the survey is saved whole in one request, so
 * the bank never holds a survey without name or questions.
 */
export const SurveyTemplateEditor = ({
  initialDraft,
  initialQuestions,
  onDone,
  onCancel,
}: IProps) => {
  const { t } = useTranslation()
  const notification = useNotification()
  const [step, setStep] = useState<1 | 2>(1)
  const [draft, setDraft] = useState(initialDraft)
  const [questions, setQuestions] = useState<DraftQuestion[]>(initialQuestions)
  const [openQuestion, setOpenQuestion] = useState<number | 'new' | null>(null)
  const [errors, setErrors] = useState<SurveyTemplateErrors>({})
  const [saving, setSaving] = useState(false)
  const nextKey = useRef(
    initialQuestions.reduce((max, { key }) => Math.max(max, key), 0) + 1
  )

  const text = (key: string, options?: Record<string, unknown>) =>
    t(`${TRANSLATION_PREFIX}.editor.${key}`, options)
  const isEditing = !!initialDraft.id
  const editorPrefix = 'administration.surveyEditor'

  const upsertQuestion = (
    current: DraftQuestion[],
    data: any,
    type: string,
    key: number
  ): DraftQuestion[] => {
    const existing = current.find((question) => question.key === key)
    const saved = { ...fromEditorSave(data, type, existing), key }
    return existing
      ? current.map((question) => (question.key === key ? saved : question))
      : [...current, saved]
  }

  const newQuestionKey = () => {
    const key = nextKey.current
    nextKey.current += 1
    return key
  }

  /** Saves the editor of the question with `key`, or a new one with key 0. */
  const handleQuestionSave = (key: number) => (data: any, type: string) => {
    const questionKey = key || newQuestionKey()
    setQuestions((current) => upsertQuestion(current, data, type, questionKey))
    setOpenQuestion(null)
    setErrors((current) => ({ ...current, questions: undefined }))
  }

  const validate = (finalQuestions: DraftQuestion[]) => {
    if (!draft.name.trim()) {
      setErrors({ name: text('errors.nameRequired') })
      setStep(1)
      return false
    }
    if (finalQuestions.length === 0) {
      setErrors({ questions: text('errors.questionsRequired') })
      setStep(2)
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validate(questions)) return

    setSaving(true)
    const payload = toSurveyTemplatePayload(draft, questions)
    try {
      if (draft.id) await surveyTemplatesService.update(draft.id, payload)
      else await surveyTemplatesService.create(payload)
      notification.success(
        text('savedTemplate', { name: payload.surveyTemplate.name })
      )
      onDone()
    } catch (error) {
      const validation = readValidationErrors(error)
      if (validation?.name) {
        setErrors({ name: text('errors.duplicateName') })
        setStep(1)
      } else if (validation) {
        // The API message is a Rails fragment without subject and has no pt/fr.
        setErrors({ questions: text('errors.invalid') })
        setStep(2)
      } else {
        notification.error(text('errors.saveFailed'))
      }
    } finally {
      setSaving(false)
    }
  }

  // Stable per render of the draft: the shared editor reloads its fields
  // whenever its `data` prop changes, which would drop an edit in progress.
  const editorQuestions = useMemo(
    () => questions.map((question, index) => toEditorQuestion(question, index)),
    [questions]
  )

  const removeQuestion = (key: number) =>
    setQuestions((current) =>
      current.filter((question) => question.key !== key)
    )

  const reorderQuestions = (from: number, to: number) =>
    setQuestions((current) => {
      const next = [...current]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })

  const detailsValues = {
    name: draft.name,
    description: draft.description,
  } as IBlockInstanceModel

  const typeLabel = (type: string) => {
    const key = type[0].toLowerCase() + type.substring(1).replace(/\s/g, '')
    return t(`administration.surveyEditor.question.${key}`)
  }

  const openIndex =
    openQuestion === 'new'
      ? questions.length + 1
      : questions.findIndex((question) => question.key === openQuestion) + 1
  const openData =
    typeof openQuestion === 'number'
      ? editorQuestions.find(
          (question) => question.questionableId === openQuestion
        )
      : undefined

  return (
    <div data-testid="survey-bank-editor">
      <SurveyEditorShell
        title={isEditing ? text('updateTitle') : text('createTitle')}
        secondaryLabel={
          step === 1 ? text('cancel') : t(`${editorPrefix}.buttons.previous`)
        }
        primaryLabel={
          step === 1 ? t(`${editorPrefix}.buttons.next`) : text('saveTemplate')
        }
        onSecondary={() => (step === 1 ? onCancel() : setStep(1))}
        onPrimary={() => (step === 1 ? setStep(2) : handleSave())}
        primaryDisabled={saving}
        primaryTestId={step === 2 ? 'survey-bank-save' : undefined}
      >
        {step === 1 ? (
          <SurveyDetails
            values={detailsValues}
            onChange={(values) =>
              setDraft((current) => ({
                ...current,
                name: values.name ?? '',
                description: values.description ?? '',
              }))
            }
            errors={{ name: errors.name ?? '' }}
          />
        ) : (
          <>
            {errors.questions && (
              <FieldError
                role="alert"
                data-testid="survey-bank-questions-error"
              >
                {errors.questions}
              </FieldError>
            )}
            <SurveyQuestionRows
              items={questions.map((question, index) => ({
                key: question.key,
                name: question.name,
                typeLabel: typeLabel(editorQuestions[index].type),
                mandatory: question.mandatory,
              }))}
              rowTestId={(index) => `survey-bank-question-${index + 1}`}
              onAdd={() => setOpenQuestion('new')}
              onEdit={(key) => setOpenQuestion(key)}
              onDelete={removeQuestion}
              onReorder={reorderQuestions}
            />
          </>
        )}
      </SurveyEditorShell>
      {openQuestion !== null && (
        <SurveyQuestionModal
          title={t(
            `${editorPrefix}.list.${
              openQuestion === 'new' ? 'add' : 'editTitle'
            }`
          )}
          index={openIndex}
          data={openData}
          blockId={undefined}
          onSave={handleQuestionSave(openQuestion === 'new' ? 0 : openQuestion)}
          onClose={() => setOpenQuestion(null)}
        />
      )}
    </div>
  )
}
